import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  MapPin, CloudSun, Leaf, BookOpen } from 'lucide-react';

const CornellUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Cornell University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#B31B1B] selection:bg-[#B31B1B] selection:text-white">
            {/* Utility Bar */}
            <div className="bg-[#222] text-gray-300 py-1 px-4 flex justify-between items-center text-xs font-bold">
                 <div className="flex gap-4">
                     <span className="flex items-center gap-1 text-white"><CloudSun size={12}/> Ithaca, NY 45°F</span>
                 </div>
                 <div className="flex gap-4">
                    <a href="#" className="hover:text-white">Student</a>
                    <a href="#" className="hover:text-white">Faculty</a>
                    <a href="#" className="hover:text-white">Staff</a>
                    <a href="#" className="hover:text-white">Alumni</a>
                 </div>
            </div>

            {/* Main Nav */}
            <nav className="bg-white py-6 px-4 md:px-8 border-b-4 border-[#B31B1B] sticky top-0 z-50 shadow-sm">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className="p-2 border-2 border-[#B31B1B] rounded-full">
                                <BookOpen className="text-[#B31B1B]" size={24} />
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[#B31B1B] font-serif font-bold text-3xl leading-none">{schoolName.split(' ')[0]}</span>
                            <span className="text-gray-500 font-sans text-xs uppercase tracking-[0.2em]">{schoolName.split(' ').slice(1).join(' ') || "University"}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] uppercase text-sm tracking-wide">
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">About</a>
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">Public Engagement</a>
                        <a href="#" className="hover:text-[#B31B1B] transition-colors">Global</a>
                    </div>

                    <div className="flex gap-4 text-[#B31B1B]">
                        <Search size={24} className="cursor-pointer" />
                        <Menu size={24} className="cursor-pointer lg:hidden" />
                    </div>
                </div>
            </nav>

            {/* Hero Section - The "Big Red" feel */}
            <header className="relative h-[700px]">
                <img 
                    src="https://images.unsplash.com/photo-1596425956976-a971d871d3a4?q=80&w=2070&auto=format&fit=crop" 
                    alt="Cornell Clocktower" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 text-white text-center">
                    <h1 className="text-5xl md:text-7xl font-bold font-serif mb-6 drop-shadow-lg">
                        Any Person ... Any Study.
                    </h1>
                    <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto font-light drop-shadow-md">
                        Since our founding, {schoolName.split(' ')[0]} has been a university where anyone can find instruction in any study.
                    </p>
                    <Button className="bg-[#B31B1B] hover:bg-[#8e1515] text-white font-bold text-lg px-10 h-16 rounded-full shadow-lg border-2 border-white/20">
                        Explore Our Colleges
                    </Button>
                </div>
            </header>

            {/* Cards Grid */}
            <section className="bg-[#f0f0f0] py-20 px-4 md:px-8">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-[#B31B1B] text-white p-8 rounded-lg shadow-md flex flex-col justify-between min-h-[300px]">
                        <Leaf size={40} className="mb-4" />
                        <h3 className="text-3xl font-bold font-serif mb-2">Sustainability</h3>
                        <p className="mb-6 opacity-90">Leading the way in climate research and campus solutions.</p>
                        <a href="#" className="font-bold underline">Learn more</a>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2 relative overflow-hidden group cursor-pointer">
                        <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070&auto=format&fit=crop" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Students" />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors"></div>
                        <div className="relative z-10 h-full flex flex-col justify-end text-white p-4">
                            <span className="uppercase text-xs font-bold tracking-widest mb-2 bg-[#B31B1B] w-fit px-2 py-1 rounded-sm">Student Life</span>
                            <h3 className="text-2xl font-bold font-serif">A vibrant campus community in the heart of the Finger Lakes.</h3>
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-lg shadow-md flex flex-col min-h-[300px]">
                        <span className="text-[#B31B1B] font-bold uppercase text-xs tracking-widest mb-4">News</span>
                        <h3 className="text-xl font-bold mb-4 hover:text-[#B31B1B] cursor-pointer">Researchers develop new biodegradable plastics from agriculture waste.</h3>
                        <p className="text-sm text-gray-500 mt-auto">October 24, 2025</p>
                    </div>
                </div>
            </section>

            {/* Verify Footer */}
            <footer className="bg-[#222] text-white pt-16 pb-8 px-8 border-t-8 border-[#B31B1B]">
                <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-12 mb-12">
                     <div className="max-w-xs">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-1 border border-white rounded-full">
                                 <BookOpen className="text-white" size={20} />
                            </div>
                            <span className="text-2xl font-serif font-bold">{schoolName}</span>
                        </div>
                        <p className="text-gray-400 text-sm">
                            Ithaca, New York 14853 <br/>
                            607-254-4636
                        </p>
                     </div>

                     <div className="flex gap-16 font-bold text-sm text-gray-300">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-[#B31B1B]">Contact</a></li>
                             <li><a href="#" className="hover:text-[#B31B1B]">Maps</a></li>
                             <li><a href="#" className="hover:text-[#B31B1B]">{schoolName.split(' ')[0]} Health</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-[#B31B1B]">Undergraduate</a></li>
                             <li><a href="#" className="hover:text-[#B31B1B]">Graduate</a></li>
                             <li><a href="#" className="hover:text-[#B31B1B]">Professional</a></li>
                         </ul>
                     </div>
                </div>
                <div className="max-w-[1400px] mx-auto border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
                    © 2026 {schoolName} · Privacy · Accessibility
                </div>
            </footer>
        </div>
    );
};

export default CornellUniversity;
